import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import OrbitalLoader from "../common/OrbitalLoader";
import GeneratingVideoPreview from "./GeneratingVideoPreview";
import { videosService } from "../../services/videos";

const POLL_MS = 4000;

const GeneratedVideoDetail = ({ videoHash, initialVideo, brandHash }) => {
  const [video, setVideo] = useState(initialVideo ?? null);
  const [loading, setLoading] = useState(!initialVideo);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!videoHash) return undefined;
    if (initialVideo && initialVideo.hash === videoHash) {
      setVideo(initialVideo);
      setLoading(false);
      return undefined;
    }

    setLoading(true);
    setError(null);
    videosService.getVideo(
      videoHash,
      (next) => {
        if (next?.brand_hash && brandHash && next.brand_hash !== brandHash) {
          setError("This video is not part of this brand.");
          setVideo(null);
        } else {
          setVideo(next);
        }
        setLoading(false);
      },
      () => {
        setError("Could not load video details.");
        setLoading(false);
      },
    );
  }, [videoHash, initialVideo, brandHash]);

  useEffect(() => {
    if (!video?.hash) return undefined;
    if (video.status === "completed" || video.status === "failed") return undefined;

    const poll = () => {
      videosService.getVideo(video.hash, (next) => setVideo(next), () => {});
    };
    const interval = setInterval(poll, POLL_MS);
    return () => clearInterval(interval);
  }, [video?.hash, video?.status]);

  if (loading) {
    return (
      <div className="py-10 text-center">
        <OrbitalLoader size={72} text="Loading video..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-4 text-theme-text opacity-80">
        {error}
      </div>
    );
  }

  if (!video) {
    return (
      <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-4 text-theme-text opacity-80">
        Video not found.
      </div>
    );
  }

  const title = video.idea_title || video.title || "Generated video";
  const isGenerating = video.status === "pending" || video.status === "generating";

  return (
    <div className="space-y-4">
      <div>
        <p className="text-xs uppercase tracking-wide text-theme-text opacity-60">Video</p>
        <h2 className="text-2xl font-semibold text-theme-text">{title}</h2>
        <p className="text-sm text-theme-text opacity-70 mt-1 capitalize">
          {video.status}
        </p>
      </div>

      {isGenerating ? (
        <div className="space-y-3">
          <GeneratingVideoPreview imageUrl={video.script_preview_url} />
          <p className="text-sm text-theme-text opacity-70 text-center">
            This can take several minutes. You can close this modal and come back later.
          </p>
        </div>
      ) : null}

      {video.status === "failed" ? (
        <div className="rounded-lg border border-theme-secondary bg-theme-secondary p-4 text-theme-text opacity-80">
          {video.status_error || "Video generation failed."}
        </div>
      ) : null}

      {video.status === "completed" && video.video_url ? (
        <div className="rounded-lg overflow-hidden border border-theme-secondary bg-black">
          <video
            src={video.video_url}
            controls
            playsInline
            className="w-full max-h-[520px] bg-black"
            poster={video.thumbnail_url || undefined}
          />
        </div>
      ) : null}

      {video.script_hash ? (
        <Link
          to={`/scripts/${encodeURIComponent(video.script_hash)}/`}
          className="inline-block text-sm text-theme-primary hover:underline"
        >
          View source script
        </Link>
      ) : null}
    </div>
  );
};

export default GeneratedVideoDetail;
